import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { useTheme } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Typography,
  Paper,
  Box,
  Chip,
  Divider
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { DEFAULT_PROVIDER, getWhatsAppProviderLabel } from "../../constants/whatsappProviders";
import useStyles from "./whatsAppModalStyles";
import useWhatsAppModal from "./useWhatsAppModal";
import TabPanel from "./TabPanel";
import TransferTab from "./TransferTab";
import ProviderCredentialsSection from "./ProviderCredentialsSection";
import AutomationSection from "./AutomationSection";
import { buildWhatsAppPayload } from "./whatsAppModalHelpers";
import QueueSelect from "../QueueSelect";
// ✅ FASE 2: Importar modal de preview de cambios
import ConfigChangePreviewModal from "../ConfigChangePreviewModal";
// ✅ NUEVO: Importar componentes mejorados de selección
import PromptSelect from "../PromptSelect";
import IntegrationSelect from "../IntegrationSelect";
// ✅ NUEVO: Importar modal de advertencia de conexión desconectada
import ConnectionWarningModal from "../ConnectionWarningModal";

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
});


const WhatsAppModal = ({ open, onClose, whatsAppId, onSave, defaultProvider = DEFAULT_PROVIDER }) => {
  const classes = useStyles();
  const theme = useTheme();
  const {
    activeTab,
    companyId,
    connectionWarningData,
    connectionWarningOpen,
    flowBuilders,
    formikInitialValues,
    handleChangeIntegration,
    handleChangePrompt,
    handleChangeQueue,
    handleCheckboxChange,
    handleClose,
    handleConfirmConfigChange,
    handleSaveWhatsApp,
    handleTabChange,
    history,
    isLoading,
    loadingFlowBuilders,
    loadingWhatsapps,
    pendingWhatsAppData,
    previewData,
    previewModalOpen,
    selectedFlowBuilder,
    selectedIntegration,
    selectedPrompt,
    selectedQueueId,
    setActiveTab,
    setConnectionWarningData,
    setConnectionWarningOpen,
    setFlowBuilders,
    setIsLoading,
    setLoadingFlowBuilders,
    setLoadingWhatsapps,
    setPendingWhatsAppData,
    setPreviewData,
    setPreviewModalOpen,
    setSelectedFlowBuilder,
    setSelectedIntegration,
    setSelectedPrompt,
    setSelectedQueueId,
    setStableInitialValues,
    setTabStates,
    setTransferQueueId,
    setTransferWhatsappId,
    setWhatsApp,
    setWhatsapps,
    stableInitialValues,
    tabStates,
    transferQueueId,
    transferWhatsappId,
    user,
    whatsApp,
    whatsapps,
  } = useWhatsAppModal({ open, onClose, whatsAppId, onSave, defaultProvider });

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
      >
        <DialogTitle>
          {whatsAppId
            ? `${i18n.t("whatsappModal.title.edit")} - ${getWhatsAppProviderLabel(whatsApp.provider || defaultProvider)}`
            : `${i18n.t("whatsappModal.title.add")} - ${getWhatsAppProviderLabel(defaultProvider)}`}
        </DialogTitle>
        
        <Formik
          key={whatsAppId || 'new'} // ✅ FORZAR RE-MOUNT solo cuando cambia el ID
          initialValues={formikInitialValues}
          enableReinitialize={true} // ✅ RE-INICIALIZAR cuando initialValues cambien (al cargar datos desde la API)
          validationSchema={SessionSchema}
          onSubmit={async (values, actions) => {
            // ✅ Validar campos requeridos manualmente antes de enviar
            // Esto evita el error "not focusable" al validar campos ocultos
            const errors = {};
            if (!values.name || values.name.trim().length === 0) {
              errors.name = 'Required';
            }
            
            if (Object.keys(errors).length > 0) {
              // Si hay errores, mostrar la pestaña que contiene el campo con error
              if (errors.name) {
                setActiveTab(0); // Ir a pestaña CONEXIÓN
              }
              actions.setErrors(errors);
              actions.setSubmitting(false);
              toast.error("Por favor completa los campos requeridos.");
              return;
            }
            
            
            setTimeout(() => {
              handleSaveWhatsApp(values); // ✅ PASAR VALORES DIRECTAMENTE
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) => {
            // ✅ Calcular estados de automatización para deshabilitar campos
            const hasPromptInForm = !!(selectedPrompt && selectedPrompt !== null && selectedPrompt !== "");
            const hasIntegrationInForm = !!(selectedIntegration && selectedIntegration !== null && selectedIntegration !== "");
            const hasFlowBuilderInForm = !!(selectedFlowBuilder && selectedFlowBuilder !== null && selectedFlowBuilder !== "");
            const hasQueueInForm = !!(selectedQueueId && selectedQueueId !== null && selectedQueueId !== "" && selectedQueueId !== 0);
            
            return (
              <Form>
              <DialogContent dividers className={classes.dialogContent}>
                {/* ✅ PESTAÑAS DE NAVEGACIÓN */}
                <Paper square style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    indicatorColor="primary"
                    textColor="primary"
                    aria-label="Configuración de WhatsApp"
                  >
                    <Tab label="Conexión" />
                    {/* ✅ ELIMINADO: Tab "Chatbot Inicial" - Ya no se usa */}
                    <Tab label="Transferencias" />
                  </Tabs>
                </Paper>

                {/* ✅ PESTAÑA 1: CONEXIÓN (OPTIMIZADA) */}
                <TabPanel value={activeTab} index={0} className={classes.tabPanel}>
                  {/* ✅ OPTIMIZADO: Header más compacto */}
                  <div className={classes.sectionHeader}>
                    <Typography variant="h6" color="primary" style={{ marginBottom: '4px' }}>
                      <span role="img" aria-label="conexión">🔌</span> Configuración
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.85rem' }}>
                      Configuración básica de la conexión de WhatsApp
                    </Typography>
                  </div>

                  <div className={classes.tabContent}>
                    {/* ✅ OPTIMIZADO: Campos en Grid de 2 columnas */}
                    <Grid container spacing={2}>
                      <ProviderCredentialsSection classes={classes} values={values} />

                      <AutomationSection
                        classes={classes}
                        selectedQueueId={selectedQueueId}
                        handleChangeQueue={handleChangeQueue}
                        selectedPrompt={selectedPrompt}
                        handleChangePrompt={handleChangePrompt}
                        selectedIntegration={selectedIntegration}
                        handleChangeIntegration={handleChangeIntegration}
                        selectedFlowBuilder={selectedFlowBuilder}
                        setSelectedFlowBuilder={setSelectedFlowBuilder}
                        setSelectedPrompt={setSelectedPrompt}
                        setSelectedIntegration={setSelectedIntegration}
                        setSelectedQueueId={setSelectedQueueId}
                        loadingFlowBuilders={loadingFlowBuilders}
                        flowBuilders={flowBuilders}
                        hasPromptInForm={hasPromptInForm}
                        hasIntegrationInForm={hasIntegrationInForm}
                        hasFlowBuilderInForm={hasFlowBuilderInForm}
                        hasQueueInForm={hasQueueInForm}
                      />

                      {/* Para qué sirve esta conexión (doc 0424 F4 · CU-8).
                          Un número personal conectado al CRM reparte a
                          departamentos y despierta al bot igual que una línea
                          de atención: así acabaron las conversaciones privadas
                          de un propietario contestadas por un asistente. */}
                      <Grid item xs={12}>
                        <Divider style={{ margin: "8px 0" }} />
                        <FormControl variant="outlined" fullWidth margin="dense">
                          <InputLabel id="connection-purpose-label">
                            Para qué sirve esta conexión
                          </InputLabel>
                          <Select
                            labelId="connection-purpose-label"
                            label="Para qué sirve esta conexión"
                            value={values.type === "internal" ? "internal" : "attention"}
                            onChange={e =>
                              setFieldValue("type", e.target.value === "internal" ? "internal" : "")
                            }
                          >
                            <MenuItem value="attention">Atención al cliente</MenuItem>
                            <MenuItem value="internal">Número interno o personal</MenuItem>
                          </Select>
                        </FormControl>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {values.type === "internal"
                            ? "Interno: las conversaciones entran al CRM, pero no se reparten a departamentos ni las contesta el bot. Piénsalo antes: conectar un número personal vuelca sus conversaciones privadas, visibles para los agentes."
                            : "Atención al cliente: comportamiento normal — reparto a departamentos y automatizaciones activas."}
                        </Typography>
                      </Grid>

                      {/* Modo seguro para Leads Web (doc 0417, Punto 4): un
                          lead convertido NUNCA debe escribirle primero a un
                          número que nunca contactó a la empresa — riesgo real
                          de bloqueo (Baileys) o cobro por plantilla (WABA).
                          Vive en la conexión, no en el formulario: el riesgo
                          depende de la línea, no de qué formulario lo trajo. */}
                      <Grid item xs={12}>
                        <Divider style={{ margin: "8px 0" }} />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!values.webLeadsInstantMessage}
                              onChange={e => setFieldValue("webLeadsInstantMessage", e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Mandar mensaje inmediato a Leads Web nuevos"
                        />
                        <Typography variant="caption" color="textSecondary" display="block">
                          {values.webLeadsInstantMessage
                            ? "Activado: en cuanto un formulario web convierte un lead, esta conexión le manda el mensaje de bienvenida de inmediato — el negocio escribe primero. Meta puede bloquear la línea o cobrar por plantilla si el número nunca contactó antes."
                            : "Modo seguro: un lead nuevo solo crea/actualiza el Contacto — nunca se crea ticket ni se manda nada. Queda en \"Solicitudes web\" esperando a que el cliente escriba primero."}
                        </Typography>
                      </Grid>
                    </Grid>
                  </div>
                  
                  {/* ✅ BOTONES DENTRO DE LA PESTAÑA CONEXIÓN - MEJOR VISIBILIDAD */}
                  {activeTab === 0 && (
                    <Box 
                      style={{ 
                        padding: theme.spacing(2, 3),
                        borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : theme.palette.divider}`,
                        backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: theme.spacing(2),
                        marginTop: theme.spacing(2),
                      }}
                    >
                      <Button
                        onClick={handleClose}
                        color="secondary"
                        disabled={isSubmitting || isLoading}
                        variant="outlined"
                        style={{
                          minWidth: '120px',
                          fontWeight: 600,
                        }}
                      >
                        {i18n.t("whatsappModal.buttons.cancel")}
                      </Button>
                      <Button
                        type="submit"
                        color="primary"
                        disabled={isSubmitting || isLoading}
                        variant="contained"
                        style={{
                          minWidth: '120px',
                          fontWeight: 600,
                          boxShadow: `0 4px 8px ${theme.palette.primary.main}40`,
                        }}
                      >
                        {whatsAppId
                          ? i18n.t("whatsappModal.buttons.okEdit")
                          : i18n.t("whatsappModal.buttons.okAdd")}
                        {((isSubmitting || isLoading)) && (
                          <CircularProgress
                            size={20}
                            style={{ marginLeft: theme.spacing(1) }}
                          />
                        )}
                      </Button>
                    </Box>
                  )}
                </TabPanel>

                {/* ✅ ELIMINADO: PESTAÑA 2: CHATBOT INICIAL - Ya no se usa */}
                {/* Los departamentos ahora trabajan de forma inteligente, no necesitan chatbot inicial */}

                {/* ✅ PESTAÑA 2: TRANSFERENCIAS (OPTIMIZADA) */}
                <TabPanel value={activeTab} index={1} className={classes.tabPanel}>
                  <TransferTab
                    classes={classes}
                    tabStates={tabStates}
                    handleCheckboxChange={handleCheckboxChange}
                    setFieldValue={setFieldValue}
                    transferQueueId={transferQueueId}
                    setTransferQueueId={setTransferQueueId}
                    transferWhatsappId={transferWhatsappId}
                    setTransferWhatsappId={setTransferWhatsappId}
                    loadingWhatsapps={loadingWhatsapps}
                    whatsapps={whatsapps}
                    theme={theme}
                  />
                </TabPanel>
              </DialogContent>

              {/* ✅ BOTONES PARA PESTAÑA TRANSFERENCIAS - Solo mostrar si NO está en pestaña CONEXIÓN */}
              {activeTab !== 0 && (
                <DialogActions className={classes.dialogActions}>
                  <Button
                    onClick={handleClose}
                    color="secondary"
                    disabled={isSubmitting || isLoading}
                    variant="outlined"
                    style={{
                      minWidth: '120px',
                      fontWeight: 600,
                    }}
                  >
                    {i18n.t("whatsappModal.buttons.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting || isLoading}
                    variant="contained"
                    style={{
                      minWidth: '120px',
                      fontWeight: 600,
                      boxShadow: `0 4px 8px ${theme.palette.primary.main}40`,
                    }}
                  >
                    {whatsAppId
                      ? i18n.t("whatsappModal.buttons.okEdit")
                      : i18n.t("whatsappModal.buttons.okAdd")}
                    {((isSubmitting || isLoading)) && (
                      <CircularProgress
                        size={20}
                        style={{ marginLeft: theme.spacing(1) }}
                      />
                    )}
                  </Button>
                </DialogActions>
              )}
              </Form>
            );
          }}
          </Formik>
      </Dialog>

      {/* ✅ NUEVO: Modal de advertencia de conexión desconectada */}
      <ConnectionWarningModal
        open={connectionWarningOpen}
        onClose={() => {
          setConnectionWarningOpen(false);
          setConnectionWarningData(null);
        }}
        connectionName={connectionWarningData?.connectionName || ""}
        connectionStatus={connectionWarningData?.connectionStatus || ""}
      />

      {/* ✅ FASE 2: Modal de preview de cambios de configuración */}
      <ConfigChangePreviewModal
        open={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPendingWhatsAppData(null);
          setPreviewData(null);
        }}
        previewData={previewData}
        onConfirm={handleConfirmConfigChange}
        loading={isLoading}
        type="whatsapp"
      />
    </div>
  );
};

export default React.memo(WhatsAppModal);
